import { Link, Navigate, Outlet, useParams, useRouterState } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { BookOpen, Building2, ChevronDown, Home, LogOut, Menu, MessageSquareText, Settings, Shield, User, Users } from 'lucide-react'
import { useState, type ComponentType } from 'react'
import { post } from '../lib/api.ts'
import { useCondo } from '../lib/hooks.ts'
import { brandStyle } from '../lib/types.ts'
import { extraNav } from '../routes/extra.tsx'
import { cn, Spinner } from './ui.tsx'
import { t } from '../lib/i18n.ts'

export interface NavItem {
  to: string
  label: string
  icon: ComponentType<{ className?: string }>
  cap?: string
  primary?: boolean
}

const BASE_NAV: NavItem[] = [
  { to: '', label: t('Início'), icon: Home, primary: true },
  { to: '/acervo', label: t('Acervo'), icon: BookOpen, primary: true },
  { to: '/assistente', label: t('Perguntar'), icon: MessageSquareText, primary: true },
]
const ADMIN_NAV: NavItem[] = [
  { to: '/membros', label: t('Membros e convites'), icon: Users, cap: 'members.view' },
  { to: '/unidades', label: t('Unidades'), icon: Building2 },
  { to: '/config', label: t('Configurações'), icon: Settings, cap: 'condo.manage' },
  { to: '/auditoria', label: t('Registro de atividades'), icon: Shield, cap: 'audit.view' },
]

export function CondoShell() {
  const { slug } = useParams({ strict: false }) as { slug: string }
  const { me, membership, info, can } = useCondo(slug)
  const path = useRouterState({ select: (s) => s.location.pathname })
  const [menu, setMenu] = useState(false)
  const [switcher, setSwitcher] = useState(false)
  const qc = useQueryClient()

  if (me.isLoading) return <div className="p-6"><Spinner /></div>
  if (!me.data?.user) return <Navigate to="/entrar" search={{ next: path } as never} />
  if (!membership) return <Navigate to="/" />

  const base = `/c/${slug}`
  const brand = info.data?.condo.brand
  const logoUrl = brand?.hasLogo ? `/api/condos/${membership.condoId}/brand/logo` : null
  const nav = [...BASE_NAV, ...extraNav].filter((n) => !n.cap || can(n.cap))
  const more = ADMIN_NAV.filter((n) => !n.cap || can(n.cap))
  const isActive = (to: string) => (to === '' ? path === base || path === `${base}/` : path.startsWith(base + to))
  const others = me.data.memberships.filter((m) => m.slug !== slug)

  const logout = async () => {
    await post('/api/auth/logout')
    qc.clear()
    location.href = '/entrar'
  }

  return (
    <div className="min-h-dvh md:grid md:grid-cols-[17rem_1fr]" style={brandStyle(brand?.color)}>
      <aside className="hidden border-r border-pauta bg-folha md:flex md:flex-col">
        <CondoTitle name={membership.name} role={t(membership.roleLabel)} brandName={brand?.name ?? null} logoUrl={logoUrl} onSwitch={others.length ? () => setSwitcher((v) => !v) : undefined} />
        {switcher && <Switcher others={others} />}
        <nav className="flex-1 overflow-y-auto px-3 py-2" aria-label={t('Seções')}>
          {nav.map((n) => (
            <SideLink key={n.to} to={base + n.to} active={isActive(n.to)} icon={n.icon} label={n.label} />
          ))}
          {more.length > 0 && <div className="my-3 border-t border-pauta" />}
          {more.map((n) => (
            <SideLink key={n.to} to={base + n.to} active={isActive(n.to)} icon={n.icon} label={n.label} />
          ))}
        </nav>
        <div className="border-t border-pauta p-3 text-[0.95rem]">
          <Link to="/perfil" className="flex items-center gap-2 rounded px-2 py-2 hover:bg-papel">
            <User className="size-4" /> {me.data.user.name}
          </Link>
          {me.data.user.isPlatformAdmin && (
            <Link to="/plataforma" className="flex items-center gap-2 rounded px-2 py-2 hover:bg-papel">
              <Shield className="size-4" />{' '}{t('Plataforma')}
            </Link>
          )}
          <button onClick={logout} className="flex w-full items-center gap-2 rounded px-2 py-2 text-left hover:bg-papel">
            <LogOut className="size-4" />{' '}{t('Sair')}
          </button>
        </div>
      </aside>

      <div className="flex min-h-dvh flex-col pb-20 md:pb-0">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-2 border-b border-pauta bg-folha/95 px-4 py-2.5 backdrop-blur md:hidden">
          <button className="flex min-w-0 items-center gap-2 text-left" onClick={() => others.length && setSwitcher((v) => !v)}>
            {logoUrl && <img src={logoUrl} alt="" className="h-7 w-auto max-w-[5.5rem] shrink-0 object-contain" />}
            <span className="truncate text-[1.05rem] font-bold">{membership.name}</span>
            {others.length > 0 && <ChevronDown className="size-4 shrink-0" />}
          </button>
          <span className="shrink-0 text-sm text-tinta-2">{t(membership.roleLabel)}</span>
        </header>
        {switcher && <div className="md:hidden"><Switcher others={others} /></div>}
        <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-5 sm:px-6 sm:py-8">
          {info.isLoading ? <Spinner /> : <Outlet />}
        </main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-pauta bg-folha pb-[env(safe-area-inset-bottom)] md:hidden" aria-label={t('Seções')}>
        {nav.filter((n) => n.primary).slice(0, 3).map((n) => (
          <Link key={n.to} to={base + n.to} className={cn('flex flex-col items-center gap-0.5 py-2 text-[0.78rem] font-semibold', isActive(n.to) ? 'text-caneta' : 'text-tinta-2')}>
            <n.icon className="size-6" />
            {n.label}
          </Link>
        ))}
        <button onClick={() => setMenu(true)} className="flex flex-col items-center gap-0.5 py-2 text-[0.78rem] font-semibold text-tinta-2">
          <Menu className="size-6" />
          {t('Mais')}
        </button>
      </nav>

      {menu && (
        <div className="fixed inset-0 z-40 bg-tinta/40 md:hidden" onClick={() => setMenu(false)}>
          <div className="absolute inset-x-0 bottom-0 max-h-[80dvh] overflow-y-auto rounded-t-2xl bg-folha p-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]" onClick={(e) => e.stopPropagation()}>
            {[...nav.filter((n) => !n.primary), ...more].map((n) => (
              <Link key={n.to} to={base + n.to} onClick={() => setMenu(false)} className="flex items-center gap-3 rounded px-3 py-3 text-[1.05rem] hover:bg-papel">
                <n.icon className="size-5 text-tinta-2" /> {n.label}
              </Link>
            ))}
            <div className="my-2 border-t border-pauta" />
            <Link to="/perfil" onClick={() => setMenu(false)} className="flex items-center gap-3 rounded px-3 py-3 hover:bg-papel">
              <User className="size-5 text-tinta-2" />{' '}{t('Meu perfil')}
            </Link>
            {me.data.user.isPlatformAdmin && (
              <Link to="/plataforma" className="flex items-center gap-3 rounded px-3 py-3 hover:bg-papel">
                <Shield className="size-5 text-tinta-2" />{' '}{t('Plataforma')}
              </Link>
            )}
            <button onClick={logout} className="flex w-full items-center gap-3 rounded px-3 py-3 text-left hover:bg-papel">
              <LogOut className="size-5 text-tinta-2" />{' '}{t('Sair')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function CondoTitle({ name, role, brandName, logoUrl, onSwitch }: { name: string; role: string; brandName: string | null; logoUrl: string | null; onSwitch?: () => void }) {
  return (
    <div className="border-b border-pauta px-5 py-5">
      {logoUrl && <img src={logoUrl} alt={brandName ?? ''} className="mb-2 h-10 w-auto max-w-[12rem] object-contain object-left" />}
      <p className="text-sm font-semibold text-caneta">{brandName ?? t('Subsíndico')}</p>
      <button disabled={!onSwitch} onClick={onSwitch} className="mt-1 flex w-full items-start justify-between gap-2 text-left">
        <span className="text-[1.15rem] font-bold leading-snug">{name}</span>
        {onSwitch && <ChevronDown className="mt-1 size-4 shrink-0" />}
      </button>
      <p className="mt-0.5 text-sm text-tinta-2">{role}</p>
    </div>
  )
}

function Switcher({ others }: { others: { slug: string; name: string; roleLabel: string }[] }) {
  return (
    <div className="border-b border-pauta bg-papel px-3 py-2">
      <p className="px-2 py-1 text-sm text-tinta-2">{t('Trocar de condomínio')}</p>
      {others.map((o) => (
        <a key={o.slug} href={`/c/${o.slug}`} className="block rounded px-2 py-2 hover:bg-folha">
          <span className="font-semibold">{o.name}</span> <span className="text-sm text-tinta-2">({t(o.roleLabel)})</span>
        </a>
      ))}
    </div>
  )
}

function SideLink({ to, active, icon: Icon, label }: { to: string; active: boolean; icon: ComponentType<{ className?: string }>; label: string }) {
  return (
    <Link to={to} className={cn('mb-0.5 flex items-center gap-3 rounded-ficha px-3 py-2.5 text-[1rem]', active ? 'bg-caneta-clara font-semibold text-caneta-escura' : 'text-tinta hover:bg-papel')} aria-current={active ? 'page' : undefined}>
      <Icon className={cn('size-5', active ? 'text-caneta' : 'text-tinta-2')} />
      {label}
    </Link>
  )
}
